export interface FreshsalesCallData {
  externalCallId?: string;
  contactId?: string;
  phoneNumber?: string;
  direction: 'inbound' | 'outbound';
  startTime?: number;
  endTime?: number;
  duration?: number;
  status?: 'ringing' | 'completed' | 'no-answer' | 'busy' | 'failed';
  recordingUrl?: string;
  notes?: string;
}

export interface FreshsalesContact {
  id: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  properties?: Record<string, any>;
}

export interface FreshsalesWidgetOptions {
  width?: number;
  height?: number;
}

class FreshsalesService {
  private isInitialized = false;
  private currentCall: FreshsalesCallData | null = null;
  private eventHandlers: Map<string, Function[]> = new Map();

  constructor() {
    console.log('[Freshsales Service] Initializing Freshsales service...');
  }

  initialize(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.isInitialized) {
        console.log('[Freshsales Service] Already initialized');
        resolve(true);
        return;
      }

      console.log('[Freshsales Service] Initializing...');

      // Simulate initialization process
      setTimeout(() => {
        this.isInitialized = true;
        console.log('[Freshsales Service] Initialized successfully');
        this.emit('ready');
        resolve(true);
      }, 100);
    });
  }

  // Event emitter methods
  on(event: string, handler: Function) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)?.push(handler);
  }

  off(event: string, handler: Function) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: any) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
    console.log(`[Freshsales Service] Event emitted: ${event}`, data);
  }

  // Call management methods
  async startOutboundCall(phoneNumber: string, contactId?: string): Promise<string> {
    if (!this.isInitialized) {
      throw new Error('Freshsales service not initialized');
    }

    const externalCallId = `freshsales-call-${Date.now()}`;

    this.currentCall = {
      phoneNumber,
      direction: 'outbound',
      contactId,
      externalCallId,
      startTime: Date.now(),
    };

    console.log('[Freshsales Service] Starting outbound call:', {
      phoneNumber,
      contactId,
      externalCallId,
    });

    this.emit('outboundCallStarted', {
      phoneNumber,
      contactId,
      externalCallId,
    });

    return externalCallId;
  }

  async startIncomingCall(
    phoneNumber: string,
    externalCallId: string,
    contactId?: string,
  ): Promise<void> {
    if (!this.isInitialized) {
      throw new Error('Freshsales service not initialized');
    }

    this.currentCall = {
      phoneNumber,
      direction: 'inbound',
      contactId,
      externalCallId,
      startTime: Date.now(),
    };

    console.log('[Freshsales Service] Starting incoming call:', {
      phoneNumber,
      contactId,
      externalCallId,
    });

    this.emit('incomingCallStarted', {
      phoneNumber,
      contactId,
      externalCallId,
    });
  }

  async endCall(status: FreshsalesCallData['status'] = 'completed'): Promise<void> {
    if (!this.currentCall) {
      console.warn('[Freshsales Service] No current call to end');
      return;
    }

    const endTime = Date.now();
    const duration = this.currentCall.startTime ? endTime - this.currentCall.startTime : 0;

    this.currentCall = {
      ...this.currentCall,
      endTime,
      duration,
      status,
    };

    console.log('[Freshsales Service] Call ended:', {
      externalCallId: this.currentCall.externalCallId,
      duration,
      status,
    });

    this.emit('callEnded', {
      callData: this.currentCall,
      duration,
      status,
    });

    // Clear current call after emitting event
    this.currentCall = null;
  }

  async updateCallNotes(notes: string): Promise<void> {
    if (!this.currentCall) {
      console.warn('[Freshsales Service] No current call to update notes');
      return;
    }

    this.currentCall.notes = notes;

    console.log('[Freshsales Service] Call notes updated:', notes);

    this.emit('callNotesUpdated', {
      externalCallId: this.currentCall.externalCallId,
      notes,
    });
  }

  async setRecordingUrl(recordingUrl: string): Promise<void> {
    if (!this.currentCall) {
      console.warn('[Freshsales Service] No current call to set recording URL');
      return;
    }

    this.currentCall.recordingUrl = recordingUrl;

    console.log('[Freshsales Service] Recording URL set:', recordingUrl);

    this.emit('recordingUrlSet', {
      externalCallId: this.currentCall.externalCallId,
      recordingUrl,
    });
  }

  async resizeWidget(options: FreshsalesWidgetOptions): Promise<void> {
    console.log('[Freshsales Service] Resizing widget:', options);

    // Post message to parent window if in iframe
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: 'resize-widget',
          data: options,
          source: 'allo-freshsales',
        },
        '*',
      );
    }

    this.emit('widgetResized', options);
  }

  getCurrentCall(): FreshsalesCallData | null {
    return this.currentCall;
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  // Utility method to send messages to parent Freshsales app
  sendMessageToParent(type: string, data: any): void {
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type,
          data,
          source: 'allo-freshsales',
        },
        '*',
      );

      console.log('[Freshsales Service] Message sent to parent:', { type, data });
    }
  }
}

export const freshsalesService = new FreshsalesService();
